import type { QueryClient } from '@tanstack/react-query';
import type { CorSelo } from '../../componentes/base';
import { numero } from '../../nucleo/formato';

export type TipoDocumento = 'arquivo' | 'contrato';
export type EstadoAssinatura = 'rascunho' | 'enviado' | 'assinado' | 'recusado' | 'cancelado' | 'expirado';
export const ESTADOS_ASSINATURA: EstadoAssinatura[] = ['rascunho', 'enviado', 'assinado', 'recusado', 'cancelado', 'expirado'];
export const COR_ASSINATURA: Record<EstadoAssinatura, CorSelo> = { rascunho: 'neutro', enviado: 'laranja', assinado: 'verde', recusado: 'perigo', cancelado: 'neutro', expirado: 'neutro' };
/** A1 (20/10): quem colhe a assinatura. A nativa é a do MalgoOS (tela e Portal); as outras, pelo e-mail do provedor. */
export type ProvedorAssinatura = 'nativa' | 'clicksign' | 'documenso';
export const PROVEDORES: ProvedorAssinatura[] = ['nativa', 'clicksign', 'documenso'];
export interface ProvedorPadrao {
  provedor: ProvedorAssinatura;
  proprio?: boolean;
  padrao: { provedor: ProvedorAssinatura };
  conectados: Record<ProvedorAssinatura, boolean>;
}
/** D24 L2: o que `GET /documentos/contratos/signatarios` devolve (quem assina o contrato gerado de modelo). */
export type PapelSignatario = 'contratante' | 'garantidor' | 'contratada';
export type AvisoSignatarios = 'sem_assinante' | 'contratante_sem_email' | 'contratante_sem_portal' | 'garantidor_nao_encontrado' | 'garantidor_sem_email' | 'garantidor_sem_portal';
export type SignatarioSugerido =
  | { papel: PapelSignatario; tipo: 'interno'; usuario_id: string; nome: string; email: string | null }
  | { papel: PapelSignatario; tipo: 'cliente'; parceiro_id: string; acesso_id: string; nome: string; email: string | null }
  | { papel: PapelSignatario; tipo: 'externo'; parceiro_id: string | null; nome: string; email: string };
export interface SugestaoSignatarios {
  documento_id: string;
  contrato_id: string;
  empresa_id: string | null;
  provedor: ProvedorAssinatura;
  provedor_origem: 'documento' | 'empresa';
  signatarios: SignatarioSugerido[];
  avisos: AvisoSignatarios[];
}
export type EstadoSignatario = 'pendente' | 'assinado' | 'recusado';
export const COR_SIGNATARIO: Record<EstadoSignatario, CorSelo> = { pendente: 'laranja', assinado: 'verde', recusado: 'perigo' };

/** Tipos de registro que a tela oferece para ligar o documento (a API aceita qualquer `<modulo>.<entidade>` que a pessoa vê). */
export const REGISTROS = [
  { tipo: 'cadastros.parceiro', modulo: 'cadastros', caminho: '/cadastros/parceiros' },
  { tipo: 'cadastros.projeto', modulo: 'cadastros', caminho: '/cadastros/projetos' },
  { tipo: 'pessoas.funcionario', modulo: 'pessoas', caminho: '/pessoas/funcionarios' },
] as const;

export interface Documento {
  id: string;
  nome: string;
  tipo: TipoDocumento;
  registro_tipo: string | null;
  registro_id: string | null;
  registro_titulo: string | null;
  modelo_id: string | null;
  versao: number;
  anexo_id: string;
  arquivo_nome: string | null;
  mime: string | null;
  tamanho: number | null;
  tags: string[];
  empresa_id: string | null;
  created_at: string;
  updated_at: string;
  criado_por_nome: string | null;
  assinatura_id: string | null;
  assinatura_estado: EstadoAssinatura | null;
  pode_editar?: boolean;
}

export interface Versao {
  versao: number;
  anexo_id: string;
  nome: string;
  mime: string;
  tamanho: number;
  sha256: string;
  created_at: string;
  criado_por_nome: string | null;
}

export interface DocumentoDetalhe extends Documento {
  versoes: Versao[];
  assinaturas: { id: string; estado: EstadoAssinatura; versao: number; created_at: string }[];
}

/** D24: modelo .docx (o arquivo do Word com campos de formulário). */
export const TIPOS_MODELO = ['contrato', 'aditivo', 'termo'] as const;
export const EVENTOS_MODELO = ['migracao_plano', 'suspensao', 'reativacao', 'troca_regime', 'rescisao', 'rescisao_inadimplencia', 'cessao'] as const;
// Review rlb-1 (SPEC-B4): a mesma ordem do regime no Cadastros (do menor ao maior porte).
export const REGIMES = ['mei', 'simples', 'presumido', 'real'] as const;
export interface CampoMapa {
  indice: number;
  chave: string;
  rotulo: string;
  obrigatorio: boolean;
  antes: string;
  depois: string;
}
export interface ChaveModelo {
  chave: string;
  rotulo: string;
  origem: 'sistema' | 'envio';
}

export interface Modelo {
  id: string;
  nome: string;
  descricao: string | null;
  corpo: string;
  ativo: boolean;
  contratos: number;
  updated_at: string;
  formato: 'texto' | 'docx';
  tipo: (typeof TIPOS_MODELO)[number];
  evento: (typeof EVENTOS_MODELO)[number] | null;
  para_empresa_id: string | null;
  servicos: string[];
  regimes: string[];
  /** D24 L3a: aditivo ou termo só para os contratos antigos (sem contrato gerado no MalgoOS). */
  so_contratos_antigos: boolean;
  versao: number;
  arquivo_anexo_id: string | null;
  campos: CampoMapa[];
}

export interface Signatario {
  id: string;
  ordem: number;
  tipo: 'interno' | 'cliente' | 'externo';
  usuario_id: string | null;
  acesso_id: string | null;
  parceiro_id: string | null;
  nome: string;
  email: string | null;
  estado: EstadoSignatario;
  decidido_em: string | null;
  ip?: string | null;
  user_agent?: string | null;
  motivo?: string | null;
  sha256_assinado?: string | null;
}

export interface LinhaTrilha {
  ordem: number;
  evento: 'criada' | 'enviada' | 'visualizada' | 'assinada' | 'recusada' | 'cancelada' | 'concluida' | 'expirada';
  signatario_id: string | null;
  ator_tipo: 'usuario' | 'cliente' | 'sistema' | 'provedor';
  ator_nome: string | null;
  ip: string | null;
  user_agent: string | null;
  sha256_documento: string | null;
  detalhe: string | null;
  hash: string;
  hash_anterior: string | null;
  created_at: string;
}

export interface Assinatura {
  id: string;
  documento_id: string;
  documento_nome: string;
  registro_tipo: string | null;
  registro_id: string | null;
  versao: number;
  anexo_id: string | null;
  sha256_documento: string | null;
  estado: EstadoAssinatura;
  mensagem: string | null;
  prazo: string | null;
  enviada_em: string | null;
  concluida_em: string | null;
  cancelada_em: string | null;
  motivo: string | null;
  created_at: string;
  criado_por_nome: string | null;
  provedor: ProvedorAssinatura;
  no_provedor: boolean;
  provedor_erro: string | null;
  anexo_assinado_id: string | null;
  signatarios: Signatario[];
  minha_pendente: boolean;
  pode_editar: boolean;
}

export interface AssinaturaDetalhe extends Assinatura {
  trilha: LinhaTrilha[];
}

export interface AcessoCliente {
  acesso_id: string;
  parceiro_id: string;
  nome: string;
  email: string | null;
}

/** Tamanho do arquivo em KB ou MB (uma casa, no formato do idioma). */
export const tamanhoArquivo = (bytes: number | null | undefined) =>
  bytes === null || bytes === undefined ? '-' : bytes < 1024 * 1024 ? `${numero(Math.max(1, Math.round(bytes / 1024)))} KB` : `${numero(bytes / (1024 * 1024), 1)} MB`;

export const invalidarDocumentos = (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['documentos'] });
